/* ===========================================================================
   tg-vehicle-selector.js
   ---------------------------------------------------------------------------
   A fitment helper for the product info column. Pick a car; the dropdowns take
   a colour and a mark appears. No sentence, no price, no add-to-cart — the
   theme's own product form still does the selling.

   THREE ANSWERS, NOT TWO
     green  ✓        we have a build cut for this car
     yellow (words)  no stock build, but we make it to order — PRO's normal
                     miss. No glyph: a mark cannot say "we will build it for
                     you", so this state gets a line of text and a popup
                     explaining the process.
     red    ✗        this product genuinely does not work in this car (a bed in
                     a compact boot). Only reachable on LEVEL/SOLO.
   Custom is available for every car, so a PRO page never shows red.

   WHAT IT DRIVES ON THE PAGE
   1. properties[Car] — a hidden input injected into the theme's product form,
      so the chosen car lands on the order. No underscore: that is what makes it
      visible in cart, checkout, confirmation, admin and packing slip.
   2. The Size option — hidden by default and set from the car. Size is a build
      tier, not a customer choice. A car with no PRO build is set to CUSTOM MADE
      and that one value is shown, so the buyer can see what they are ordering.
      If the value cannot be found the row is left visible rather than leaving
      the buyer stuck with a hidden control they cannot reach.

   THEME EDITOR
     Shopify re-renders a section on every settings change and the replacement
     markup is inert unless something re-initialises it. boot() is bound to
     shopify:section:load, and config is read per section so two instances on
     one page cannot read each other's settings.

   Asset. Loaded once, guarded. No dependencies.
   =========================================================================== */
(function () {
  'use strict';
  if (window.__tgVehicleSelectorReady) return;
  window.__tgVehicleSelectorReady = true;
  window.__tgSelectorVersion = '2026-09-06 custom-build';

  var LS_KEY = 'tg_vehicle_v1';
  var PROP = 'Car';           // no underscore -> visible everywhere. Do not rename.

  /* George's own words for the made-to-order process. Overridable from the
     block settings via customLead / customCta / customIntro / customSteps so
     the copy can change without touching this file. */
  var CUSTOM_LEAD  = 'PRO can be made custom for your car';
  var CUSTOM_CTA   = 'How custom made works';
  var CUSTOM_INTRO = 'Please tell us your car model and year.';
  var CUSTOM_STEPS = [
    'Discuss the details with us: options, shipping, total cost, and timeframes.',
    'To reserve your design, make a payment on this page. Alternatively, you can pay only the first 15% to adjust the design using the code: Designfirst',
    'Design and fitting: We find a similar car, perform a 3D scan, and ensure a perfect fit and maximum efficiency. This process usually takes 2-3 weeks. Then, with your approval, we will finalise the design.',
    'Complete your payment: If you used the code, pay the remaining balance.',
    'Manufacturing and shipping: We manufacture your custom platform bed and ship it to you. Usually, it takes 5-7 weeks.',
    'Enjoy your camping!'
  ];

  /* ---------------------------------------------------------------- helpers */
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function readJSON(id) {
    if (!id) return null;
    var n = document.getElementById(id);
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }
  function save(o) { try { localStorage.setItem(LS_KEY, JSON.stringify(o)); } catch (e) {} }
  function load() { try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (e) { return null; } }
  function fire(node, type) {
    var e;
    try { e = new Event(type, { bubbles: true }); }
    catch (err) { e = document.createEvent('Event'); e.initEvent(type, true, false); }
    node.dispatchEvent(e);
  }

  /* ------------------------------------------------------- fitment rules --- */
  /* The ONLY place these live.
     LEVEL bed = grade 1 or 2 · BOX = grade 1, 2 or 3 · PRO = the exact-fit list

     row.pro is the GENERATION list. A generation carrying s/d is one PRO is cut
     for; a generation marked no:true exists but has no PRO build (Tesla Model Y
     Long). */
  function proFit(row, gen) {
    if (!row || !row.pro) return null;
    var f = null;
    if (gen && row.pro[gen]) f = row.pro[gen];
    else {
      var keys = Object.keys(row.pro);
      if (!gen && keys.length === 1) f = row.pro[keys[0]];
    }
    if (!f || f.no) return null;
    return f;
  }
  function verdictFor(role, row, gen) {
    var g = row ? row.g : null;
    /* PRO: cut for it, or made to order. Never a flat no. */
    if (role === 'pro') return proFit(row, gen) ? 'yes' : 'custom';
    /* CORE is cut per car like PRO, so it rides PRO's list — plus the cars
       row.core marks, which are the ones a low-profile bed reaches and a
       raised one does not. Off that list it says nothing: CORE is not the
       made-to-order product, and claiming it is would be a promise we have
       not agreed to keep. */
    /* CORE is cut per car like PRO, so it rides PRO's list — plus the cars
       row.core marks, which are the ones a low-profile bed reaches and a
       raised one does not. Off that list it is made to order, at a surcharge
       carried by a variant on the product so that every market converts it. */
    if (role === 'core') {
      if (row && row.core) return 'yes';
      return proFit(row, gen) ? 'yes' : 'custom';
    }
    /* SOLO is deliberately narrow while the layout settles: the cars named in
       row.solo and nothing else, and never made to order. Saying "we can build
       you one" for a product we are not yet building is a promise, not a sale.
       row.solo is either true for the whole model or a list of generations. */
    if (role === 'solo') {
      var so = row ? row.solo : null;
      if (!so) return 'no';
      if (Object.prototype.toString.call(so) === '[object Array]') {
        if (!gen) return so.length ? 'yes' : 'no';   // model-level question
        return so.indexOf(gen) > -1 ? 'yes' : 'no';
      }
      return 'yes';
    }
    /* CAB was discontinued in September 2026. The rule is gone rather than left
       returning 'no', so that a stray "cab" in a card list is treated as an
       unknown role and simply ignored, not rendered as a product that refuses
       every car. */
    if (role === 'level') {
      if (g === 1 || g === 2) return 'yes';
      if (g === 3) return 'no';
      return 'unknown';
    }
    if (role === 'box') return (g === 1 || g === 2 || g === 3) ? 'yes' : 'unknown';
    return 'unknown';
  }
  function fitsAny(role, row) {
    if (!role || !row) return false;
    if (role === 'solo') return !!row.solo;
    if (role === 'pro' || role === 'core') {
      if (role === 'core' && row.core) return true;
      if (!row.pro) return false;
      var k = Object.keys(row.pro);
      for (var i = 0; i < k.length; i++) if (!row.pro[k[i]].no) return true;
      return false;
    }
    return verdictFor(role, row, '') === 'yes';
  }
  /* A generation's own value wins over the model's. */
  function attr(row, gen, key) {
    if (row && row.pro && gen && row.pro[gen] && row.pro[gen][key]) return row.pro[gen][key];
    return row ? row[key] : null;
  }

  /* The fitment rules are shared, not copied. The collection-page chooser reads
     them from here so that "does a LEVEL fit a Nissan Leaf?" has exactly one
     answer on the site, and changing it means changing one function. */
  window.TGFit = { proFit: proFit, verdictFor: verdictFor, fitsAny: fitsAny, attr: attr };

  /* Makes George sells most of go first, everything else A-Z. */
  function orderMakes(list, pinned) {
    var top = (pinned || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
    var head = top.filter(function (m) { return list.indexOf(m) > -1; });
    var tail = list.filter(function (m) { return head.indexOf(m) === -1; })
                   .sort(function (a, b) { return a.localeCompare(b); });
    return head.concat(tail);
  }

  /* ------------------------------------------ the theme's variant picker --- */
  /* Dawn renders each option either as a fieldset of radio pills or as a
     select. Match on the visible label so both shapes work, and so a renamed
     option only needs the setting changed, not this file. */
  function findOptionRow(name) {
    if (!name) return null;
    var sel = document.querySelector('select[name="options[' + name + ']"]');
    if (sel) {
      return { row: sel.closest ? (sel.closest('.product-form__input') || sel.parentNode) : sel.parentNode,
               control: sel, kind: 'select' };
    }
    var want = String(name).trim().toLowerCase();
    var rows = $$('variant-selects fieldset, .product-form__input');
    for (var i = 0; i < rows.length; i++) {
      var lab = rows[i].querySelector('legend, .form__label');
      if (!lab) continue;
      var txt = (lab.textContent || '').trim().toLowerCase();
      if (txt === want || txt.indexOf(want) === 0) {
        if (rows[i].querySelector('input[type="radio"]')) {
          return { row: rows[i], control: rows[i], kind: 'radio' };
        }
        /* Same row, rendered as a dropdown whose name we did not recognise —
           older Dawn names the select after the option index, not the option. */
        var s = rows[i].querySelector('select');
        if (s) return { row: rows[i], control: s, kind: 'select' };
      }
    }
    return null;
  }
  /* Match the option value loosely. An option typed in Shopify as "+100 mm "
     with a trailing space, or "standard" in lower case, is the same value to a
     human and a silent miss to ===. A miss here leaves the row on the page, so
     being strict costs a visible bug and buys nothing. */
  function sameValue(a, b) {
    if (a === b) return true;
    return String(a).trim().toLowerCase().replace(/\s+/g, ' ') ===
           String(b).trim().toLowerCase().replace(/\s+/g, ' ');
  }
  /* Is this value on the picker at all? Read-only — it selects nothing. */
  function hasOptionValue(found, value) {
    if (!found || !value) return false;
    var sel = found.kind === 'select' ? 'option' : 'input[type="radio"]';
    return $$(sel, found.control).some(function (n) { return sameValue(n.value, value); });
  }
  function setOptionValue(found, value) {
    if (!found || !value) return false;
    if (found.kind === 'select') {
      var hit = null;
      $$('option', found.control).forEach(function (o) {
        if (hit === null && sameValue(o.value, value)) hit = o.value;
      });
      if (hit === null) return false;
      if (found.control.value !== hit) {
        found.control.value = hit;
        fire(found.control, 'change');
      }
      return true;
    }
    var inputs = $$('input[type="radio"]', found.control);
    for (var i = 0; i < inputs.length; i++) {
      if (sameValue(inputs[i].value, value)) {
        if (!inputs[i].checked) { inputs[i].checked = true; fire(inputs[i], 'change'); }
        return true;
      }
    }
    return false;
  }
  /* Dawn pills are input + label[for]; the label is the visible part. */
  function labelFor(found, input) {
    if (!input.id) return null;
    return found.control.querySelector('label[for="' + input.id + '"]');
  }
  function showOnlyValue(found, value) {
    if (!found || found.kind !== 'radio') return false;
    var kept = false;
    $$('input[type="radio"]', found.control).forEach(function (inp) {
      var lab = labelFor(found, inp);
      var keep = sameValue(inp.value, value);
      if (lab) lab.style.display = keep ? '' : 'none';
      if (keep) kept = true;
    });
    return kept;
  }
  function showAllValues(found) {
    if (!found || found.kind !== 'radio') return;
    $$('input[type="radio"]', found.control).forEach(function (inp) {
      var lab = labelFor(found, inp);
      if (lab) lab.style.display = '';
    });
  }

  /* ============================================================ the widget */
  function Selector(root, data, cfg) {
    this.root = root;
    this.data = data;
    this.cfg = cfg;
    this.products = cfg.products || {};
    this.role = cfg.role || '';
    this.sizeOption = cfg.sizeOption || 'Size';
    this.customValue = cfg.customValue || 'CUSTOM MADE';
    /* Elevation is driven by default on every family except PRO.
       It used to require the option name to be typed into the block settings,
       which meant that leaving the field alone — the normal thing to do —
       silently switched the whole feature off, and the row stayed on the page.
       Now the default follows the role: PRO leaves the choice to the buyer,
       everything else is decided by the car. Type an option name to point it
       at a differently-named option, or "none" to switch it off. */
    this.elevOption = String(cfg.elevOption == null ? '' : cfg.elevOption).trim();
    if (!this.elevOption && this.role !== 'pro') this.elevOption = 'Elevation';
    if (/^(none|off|no)$/i.test(this.elevOption)) this.elevOption = '';
    /* On by default. This is the line that used to arrive as an email, and the
       emails were converting — so it has to be present unless it is switched
       off deliberately, not absent unless someone remembers to switch it on. */
    /* A card's SLOT and the RULE that judges it are two different things, and
       treating them as one is why LEVEL SOLO disappeared.

       The "solo" slot holds LEVEL SOLO — the adjustable 50/50 that fits most
       SUVs — but it was being judged by the SOLO rule, which only says yes for
       cars named in the data, and only the Model Y is. So LEVEL SOLO answered
       "no" for every Forester, RAV4 and EV6 and was filtered straight out of
       the alternatives. Nobody was ever shown it except Model Y owners.

       The slot now says which rule applies, so the card can point at one
       product and be judged by another's rule. Default below: the solo slot is
       judged as LEVEL, because that is what is in it. */
    this.altsStyle = cfg.altsStyle || 'link';   // 'link' = popup, 'cards' = inline grid
    this.rules = cfg.rules || {};
    if (!this.rules.solo)  this.rules.solo  = 'level';   // LEVEL SOLO — fits most SUVs
    if (!this.rules.soloy) this.rules.soloy = 'solo';    // SOLO Model Y — that list only
    this.askMsg   = cfg.askMsg === '' ? '' :
                    (cfg.askMsg || 'Not sure? Tell us your car and we will check it for you →');
    this.askEmail = cfg.askEmail || 'info@teraglide.com';
    /* Opt-in, not opt-out. An older cached config that predates the change
       carries no requireCar at all, and the old `!== false` turned the gate on
       for exactly those pages. Blocking the buy button has to be something
       someone switched on deliberately. */
    this.requireCar = cfg.requireCar === true;
    this.requireMsg = cfg.requireMsg || 'Choose your car first — we build each platform for one vehicle.';
    this.noFitMsg   = cfg.noFitMsg   || 'This one does not fit that car. Pick a platform that does.';
    this.elevDeep   = cfg.elevDeep   || '+100 mm';
    this.elevNormal = cfg.elevNormal || 'Standard';
    this.state = { make: '', model: '', gen: '' };
    this.build();
    this.restore();
    /* Started here rather than from applySize, which only ever runs on PRO. */
    this.watchPicker();
  }

  Selector.prototype.build = function () {
    var self = this;
    var picker = el('div', 'tg-vs__picker');

    this.mk = el('select', 'tg-vs__select');
    this.md = el('select', 'tg-vs__select');
    this.gn = el('select', 'tg-vs__select');

    [['Make', this.mk], ['Model', this.md], ['Generation', this.gn]].forEach(function (pair) {
      var wrap = el('span', 'tg-vs__field');
      pair[1].setAttribute('aria-label', pair[0]);
      wrap.appendChild(pair[1]);
      picker.appendChild(wrap);
      pair[1].__wrap = wrap;
    });
    this.gn.__wrap.hidden = true;

    this.mark = el('span', 'tg-vs__mark');
    this.mark.setAttribute('aria-live', 'polite');
    picker.appendChild(this.mark);

    this.picker = picker;
    this.root.appendChild(picker);
    this.out = el('div', 'tg-vs__out');
    this.root.appendChild(this.out);

    var all = Object.keys(this.data);
    var pinnedCount = (this.cfg.pinnedMakes || []).filter(function (m) {
      return all.indexOf(String(m).trim()) > -1;
    }).length;
    this.fill(this.mk, 'Your make', orderMakes(all, this.cfg.pinnedMakes), pinnedCount);
    this.fillModels();

    /* Size is never a customer choice — get it out of the way immediately,
       before any car is picked, so the page never flashes S / M / L. */
    this.applySize(null);

    this.mk.addEventListener('change', function () {
      self.state.make = this.value;
      self.state.model = ''; self.state.gen = '';
      self.onMake();
    });
    this.md.addEventListener('change', function () {
      self.state.model = this.value;
      self.state.gen = '';
      self.onModel();
    });
    this.gn.addEventListener('change', function () {
      self.state.gen = this.value;
      self.render();
    });
  };

  Selector.prototype.fill = function (sel, placeholder, list, dividerAfter) {
    sel.innerHTML = '';
    sel.appendChild(new Option(placeholder, ''));
    list.forEach(function (v, i) {
      if (dividerAfter && i === dividerAfter) {
        var d = new Option('──────────', '');
        d.disabled = true;
        sel.appendChild(d);
      }
      sel.appendChild(new Option(v, v));
    });
    sel.disabled = !list.length;
  };

  Selector.prototype.onMake = function () {
    this.fillModels();
    this.gn.__wrap.hidden = true;
    this.out.innerHTML = '';
    this.setVerdict('');
    this.syncForm(false);
    this.applySize(null);
    this.persist();
  };

  /* Model names, plain.
     ------------------------------------------------------------------
     The tick that used to sit in front of a fitting model is GONE. It
     read as "✓ Model Y" against "  Model S", with the non-fitting names
     padded by two spaces to keep the column straight.

     Two reasons it was worth losing. A tick inside a <select> cannot be
     styled or explained — the buyer sees a mark with no legend and has
     to infer what it means, and on a native mobile picker it just looks
     like a stray character. And it answered a question nobody had asked
     yet: the point of the selector is that you choose YOUR car and are
     then told the answer, not that you scan a list for pre-approved
     ones. That answer still arrives, properly, in the verdict mark
     beside the picker — see paintMark(), which has room for a glyph, a
     colour and a screen-reader sentence.

     If you ever want it back, the old line was:
       var fits = mark && fitsAny(self.role, self.data[self.state.make][m]);
       sel.appendChild(new Option((fits ? '✓ ' : mark ? '  ' : '') + m, m));
     ------------------------------------------------------------------ */
  Selector.prototype.fillModels = function () {
    var sel = this.md;
    var models = this.state.make
      ? Object.keys(this.data[this.state.make]).sort(function (a, b) { return a.localeCompare(b); })
      : [];

    sel.innerHTML = '';
    sel.appendChild(new Option('Model', ''));
    models.forEach(function (m) {
      sel.appendChild(new Option(m, m));
    });
    sel.disabled = !models.length;
  };

  /* Generation matters to PRO always, and to anything else only when a
     generation changes the answer — a deeper floor or a condition. Asking a
     LEVEL buyer which Forester they own would be a question with no
     consequence; asking which Model Y they own is not. */
  Selector.prototype.needsGen = function (row) {
    /* CORE is cut per car on PRO's generation list, so the generation decides
       its answer and its size exactly as it does PRO's. Without this the CORE
       page never asked, proFit() could not choose between a model's
       generations, and every multi-generation car on the list (Outback,
       Forester, RAV4, Land Cruiser...) came out as "made to order". */
    var rule = this.role ? this.ruleFor(this.role) : null;
    if (rule === 'pro' || rule === 'core') return true;
    if (row && row.pro) {
      var k = Object.keys(row.pro);
      for (var i = 0; i < k.length; i++) if (row.pro[k[i]].l || row.pro[k[i]].n) return true;
    }
    if (this.role) return false;
    return (this.cfg.order || []).indexOf('pro') > -1;
  };

  Selector.prototype.gensFor = function (row) {
    return (row && row.pro && this.needsGen(row)) ? Object.keys(row.pro) : [];
  };

  /* YEAR, WHERE THE DATA HAS NO GENERATIONS.
     Most models in the data carry no generation list — only the dozen PRO is
     cut for do. For those the third dropdown used to stay hidden, so an order
     arrived as "Kia Carnival" with no hint which one. Now it offers the model
     year instead, newest first, and the year rides along in the Car property:
     "Kia Carnival 2021".

     Optional on purpose. Nothing in the fitment rules depends on the year —
     a model without generations fits or not as a whole — so it is information
     for us, not a gate for the buyer. Models that DO have generations keep
     them, required or not exactly as before; years never replace them.

     Stored in state.gen. Safe because every rule that reads gen looks it up in
     row.pro, which these models do not have. */
  var YEAR_FROM = 2010, YEAR_TO = 2027;
  Selector.prototype.yearsFor = function (row) {
    if (!row || row.pro) return [];
    var from = parseInt(this.cfg.yearFrom, 10) || YEAR_FROM;
    var to = parseInt(this.cfg.yearTo, 10) || YEAR_TO;
    var out = [];
    for (var y = to; y >= from; y--) out.push(String(y));
    return out;
  };
  Selector.prototype.showGen = function (label, list) {
    this.fill(this.gn, label, list);
    this.gn.setAttribute('aria-label', label);
    this.gn.__wrap.hidden = false;
  };

  Selector.prototype.onModel = function () {
    var row = this.row();
    var gens = this.gensFor(row);
    if (gens.length > 1) {
      this.showGen('Generation', gens);
      this.out.innerHTML = '';
      this.setVerdict('');
      this.syncForm(false);
      this.applySize(null);
      this.persist();
      return;
    }
    var years = this.yearsFor(row);
    if (years.length) {
      this.showGen('Year', years);           // optional — the answer shows now
      this.state.gen = '';
      this.render();
      return;
    }
    this.gn.__wrap.hidden = true;
    this.state.gen = gens.length === 1 ? gens[0] : '';
    this.render();
  };

  Selector.prototype.row = function () {
    var m = this.data[this.state.make];
    return m ? m[this.state.model] : null;
  };
  Selector.prototype.carName = function () {
    var s = this.state;
    var n = s.make + ' ' + s.model;
    if (s.gen) n += ' ' + s.gen;
    return n.trim();
  };
  Selector.prototype.persist = function () {
    if (this.state.make && this.state.model) save(this.state);
  };

  /* Remembered car, else the configured default. */
  Selector.prototype.startState = function () {
    var s = load();
    if (s && s.make && this.data[s.make] && this.data[s.make][s.model]) return s;
    var d = String(this.cfg.defaultCar || '').split('|').map(function (t) { return t.trim(); });
    if (d[0] && this.data[d[0]] && this.data[d[0]][d[1]]) {
      return { make: d[0], model: d[1], gen: d[2] || '' };
    }
    return null;
  };

  Selector.prototype.restore = function () {
    var s = this.startState();
    if (!s) return;
    this.mk.value = s.make; this.state.make = s.make;
    this.onMake();
    this.md.value = s.model; this.state.model = s.model;
    var gens = this.gensFor(this.row());
    var years = gens.length ? [] : this.yearsFor(this.row());
    if (years.length) {
      this.showGen('Year', years);
      var y = s.gen ? String(s.gen) : '';
      this.state.gen = years.indexOf(y) > -1 ? y : '';
      this.gn.value = this.state.gen;
      this.render();
      return;
    }
    if (gens.length > 1) {
      this.showGen('Generation', gens);
      /* Generation names get renamed in the data ("1st gen" became
         "1st gen (5-seat, 2020–2024)"), but the theme's Default car setting and
         every returning buyer's saved car still hold the old name. Take an
         exact match, else the one generation the old name is the start of. */
      var want = s.gen ? String(s.gen) : '';
      var hit = gens.indexOf(want) > -1 ? want : '';
      if (want && !hit) {
        var stem = want.replace(/\)\s*$/, '');
        var m = gens.filter(function (g) { return g.indexOf(stem) === 0; });
        if (m.length === 1) hit = m[0];
      }
      if (hit) { this.gn.value = hit; this.state.gen = hit; }
    } else {
      this.state.gen = gens.length === 1 ? gens[0] : '';
    }
    if (this.state.gen || gens.length <= 1) this.render();
  };

  /* ------------------------------------------------------------- rendering */
  Selector.prototype.render = function () {
    this.persist();
    var row = this.row();
    this.out.innerHTML = '';
    if (!row) {
      this.setVerdict(''); this.syncForm(false);
      this.applySize(null); this.applyElev(null);
      if (this.requireCar && this.role) this.gateAddToCart(this.requireMsg);
      return;
    }

    var self = this;
    var gen = this.state.gen;
    var order = (this.cfg.order || ['pro', 'level', 'box'])
                  .map(function (s) { return String(s).trim(); }).filter(Boolean);

    var mine = this.role;
    var myVerdict = mine ? verdictFor(this.ruleFor(mine), row, gen) : null;

    /* THE OTHER PRODUCTS LIST IS A MODEL-LEVEL QUESTION.
       ------------------------------------------------------------------
       "What else do we make for a Subaru Forester" is asked about the
       MODEL. The generation belongs to the product being configured on
       this page, not to the list of other things that fit the car.

       Filtering it with verdictFor(role, row, gen) tied that list to the
       generation, and proFit() only answers without one when a model has
       exactly ONE generation:

           if (!gen && keys.length === 1) f = row.pro[keys[0]];

       So PRO silently vanished from this list for every model with more
       than one generation until a generation was picked — and on a LEVEL
       page nobody picks one, because LEVEL does not ask.

       That is 9 of the 12 PRO models: Everest, Forester, Outback,
       Model Y, Fortuner, Highlander, Land Cruiser, RAV4, Kodiaq. Every
       Toyota. Only CR-V, IONIQ 5 and Leaf showed, and only because they
       happen to have one generation each.

       fitsAny() is the model-level form of the same rules and already
       exists — it asks "is this built for ANY generation of this model",
       which is the question actually being answered here.

       The page's OWN verdict above still uses verdictFor with the
       generation, so nothing gets less precise where precision matters. */
    var alts = order.filter(function (r) {
      return r !== mine && self.products[r] &&
             fitsAny(self.ruleFor(r), row);
    });

    var car = this.state.make + ' ' + this.state.model;

    if (myVerdict === 'yes') {
      this.setVerdict('yes', 'Fits your ' + car);
      this.syncForm(true);
    } else if (myVerdict === 'custom') {
      this.setVerdict('custom', 'Made to order for your ' + car);
      this.syncForm(true);
    } else if (myVerdict) {
      this.setVerdict('no', 'Not a bed in the ' + this.state.model + ', storage only');
      this.syncForm(false);
    } else {
      this.syncForm(false);
      this.setVerdict(alts.length ? 'yes' : 'no',
        alts.length ? 'We build for the ' + car : 'Not built for the ' + car);
    }

    this.applySize(row);
    this.applyElev(row);

    if (this.requireCar && this.role) {
      if (!this.carIsComplete())      this.gateAddToCart(this.requireMsg);
      else if (myVerdict === 'no')    this.gateAddToCart(this.noFitMsg);
      else                            this.gateAddToCart(null);
    }

    if (myVerdict === 'custom') this.out.appendChild(this.madeToOrderBlock(car));

    /* A way back to a human, on every answer that is not a clean yes.

       Before the selector existed, a buyer who was unsure emailed to ask if it
       fitted, and that email was how anything non-Tesla got sold — someone
       asked, we answered, they bought. The selector answered the question and
       took the conversation with it: "made to order, twelve weeks" with no
       reply-to is a dead end, not an answer. This puts the door back, with the
       car already in the subject line so the reply costs them nothing. */
    if (myVerdict !== 'yes' && this.askMsg) {
      this.out.appendChild(this.askUsLine(car));
    }

    /* One quiet link, and the alternatives behind a click.

       This used to open out into a grid of cards whenever the page's own
       product was not a clean yes. That was deliberate — if PRO cannot fit your
       car, burying the things that can behind a click loses people — but it is
       also what turns the panel into a wall of other products, and it fires far
       more often than intended, because a page that does not know which product
       it is has no verdict and lands here every time.

       Default is the link. Set "Alternatives" to cards to get the old expanded
       behaviour back on the answers that are not a yes. */
    if (alts.length) {
      var inline = (this.altsStyle === 'cards') && myVerdict !== 'yes';
      this.out.appendChild(inline ? this.fitCards(alts, true)
                                  : this.alsoFitsLink(alts, car));
    }

    /* One note, not two. The deep-floor line is a generic stand-in for the
       extended legs; a hand-written note on the same car says the same thing
       better and usually says more. Printing both — "This floor sits deeper..."
       directly above "Third row folded. Extended legs for the deeper floor." —
       is the page repeating itself. The specific note wins; the generic one
       only appears when there is nothing better. */
    var note = attr(row, gen, 'n');
    if (!note && attr(row, gen, 'l') === 'ext') {
      note = 'This floor sits deeper — the platform is built with extended legs.';
    }
    if (note) this.out.appendChild(el('p', 'tg-vs__note', note));
    if (!alts.length && myVerdict !== 'yes' && myVerdict !== 'custom') {
      this.out.appendChild(this.customLine());
    }
  };

  /* state: 'yes' | 'custom' | 'no' | '' (cleared). Paints the dropdowns and
     shows the mark. `sr` is the same answer in words, for screen readers —
     removing the sentence must not remove the meaning, and the three glyphs
     differ in shape as well as colour. */
  Selector.prototype.setVerdict = function (state, sr) {
    this.picker.className = 'tg-vs__picker' + (state ? ' is-' + state : '');
    this.mark.innerHTML = '';
    if (!state) return;
    /* 'custom' gets no glyph — a mark cannot say "we will build it for you",
       so that state is carried by the yellow border and a line of text under
       the picker instead. */
    if (state !== 'custom') {
      var g = el('span', 'tg-vs__glyph', state === 'yes' ? '✓' : '✗');
      g.setAttribute('aria-hidden', 'true');
      this.mark.appendChild(g);
    }
    this.mark.appendChild(el('span', 'tg-vs__sr', sr || ''));
  };

  /* -------------------------------------------------------- the Size axis */
  /* PRO pages only. Hidden by default and whenever the car has a build. When
     there is no build the value becomes CUSTOM MADE and that single pill is
     shown, so the buyer can see they are ordering something made for them.

     WHY THIS IS TWO METHODS AND AN OBSERVER
     Selecting a variant makes Dawn re-fetch the section and REPLACE the whole
     variant picker. Anything we styled is thrown away with the old nodes, so a
     one-shot display:none survives only when the value happened to be selected
     already — which is exactly the "sometimes it hides, sometimes not" you saw,
     and why S / M / L reappeared alongside CUSTOM MADE. So: applySize decides
     what we want, paintSize applies it to whatever markup is on the page now,
     and a MutationObserver repaints after every one of Dawn's re-renders. */
  /* Elevation, where the car decides it rather than the buyer.
     A deep boot floor needs the taller legs, so that is fitment, not taste —
     the data marks it with l:"ext". PRO is different: there 200 vs 250 mm is a
     real preference, headroom against storage, and the buyer keeps it. That is
     why this is opt-in per product: leave the option name blank and the
     selector does not touch the picker at all. */
  /* Nothing goes in the basket until we know the car.
     ---------------------------------------------------------------------
     Every order so far has arrived without a car on it, and each one costs
     an email to the buyer asking what they drive. The button is disabled
     until the make, the model and — where it matters — the generation are
     all chosen, and again if the answer is that this platform does not fit
     that car. Selling a bed that cannot go in the boot is worse than not
     selling one.

     Disabled, not hidden. A greyed button with a line of text under it says
     what to do next; a missing button just looks broken.

     The delivery calendar hijacks the same button, so this listens in the
     capture phase on document, which runs before any handler bound to the
     form or the button itself. */
  Selector.prototype.gateAddToCart = function (state) {
    this.gateState = state;               // null = allow, otherwise a reason
    var msg = state;
    $$('form[action*="/cart/add"]').forEach(function (form) {
      var btn = form.querySelector('[name="add"], .product-form__submit, button[type="submit"]');
      if (!btn) return;
      var note = form.querySelector('.tg-vs__gate');
      if (!msg) {
        if (btn.hasAttribute('data-tg-gated')) {
          btn.removeAttribute('data-tg-gated');
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
        if (note && note.parentNode) note.parentNode.removeChild(note);
        return;
      }
      btn.setAttribute('data-tg-gated', '1');
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      if (!note) {
        note = el('p', 'tg-vs__gate');
        note.setAttribute('role', 'status');
        btn.parentNode.insertBefore(note, btn.nextSibling);
      }
      if (note.textContent !== msg) note.textContent = msg;
    });
    this.guardGate();
  };

  Selector.prototype.guardGate = function () {
    if (this.gateGuarded) return;
    this.gateGuarded = true;
    var self = this;
    function stop(e) {
      if (!self.gateState) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var note = document.querySelector('.tg-vs__gate');
      if (note && note.scrollIntoView) note.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    document.addEventListener('submit', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('form[action*="/cart/add"]')) stop(e);
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[name="add"], .product-form__submit, button[type="submit"]')) stop(e);
    }, true);
  };

  /* Complete means: make, model, and the generation where the generation
     changes the answer. Anything less and we would be guessing. */
  Selector.prototype.carIsComplete = function () {
    if (!this.state.make || !this.state.model) return false;
    var row = this.row();
    if (!row) return false;
    if (this.needsGen(row) && this.gensFor(row).length > 1 && !this.state.gen) return false;
    return true;
  };

  Selector.prototype.applyElev = function (row) {
    if (!this.elevOption) return;
    if (!row) {
      this.elevWant = null;
    } else {
      var deep = attr(row, this.state.gen, 'l') === 'ext';
      this.elevWant = { value: deep ? this.elevDeep : this.elevNormal };
    }
    this.paintElev();
  };

  Selector.prototype.paintElev = function () {
    if (!this.elevOption) return;
    var found = findOptionRow(this.elevOption);   // re-find: nodes may be new
    if (!found) return;
    showAllValues(found);

    /* Only ever touch an option we recognise. On these products elevation
       means "Standard or +100 mm", and the car decides it. The Model Y SOLO
       has an option with the same NAME whose values are 20 cm and 25 cm —
       there it is a real preference, so the row is left exactly as the theme
       rendered it. Matching on the values, not the name, is what tells the
       two apart. */
    if (!hasOptionValue(found, this.elevDeep) &&
        !hasOptionValue(found, this.elevNormal)) {
      found.row.style.display = '';
      return;
    }

    /* No car yet. Hidden, because on these products the buyer never chooses
       this — the boot floor does. Showing a control that is about to be
       overwritten invites a decision that does not count. Nothing is being
       taken away: Add to cart is gated until the car is picked anyway, and
       if this script never runs the row simply stays visible. */
    var want = this.elevWant;
    if (!want || !want.value) {
      found.row.style.display = 'none';
      return;
    }

    /* Hide only on success. A hidden control the buyer cannot reach is worse
       than one that looks redundant. */
    found.row.style.display = setOptionValue(found, want.value) ? 'none' : '';
  };

  /* Size is never the buyer's choice — it is a build tier we derive from the
     car — so the row is hidden whenever this product has one, not only when
     the page knows which product it is.

     It used to bail on `role !== 'pro'`, which meant one wrong setting (a PRO
     handle pointing at a draft product) left S / M / L / CUSTOM MADE sitting on
     the page for the buyer to guess at. Hidden is the safe default; the row
     comes back only if we try to set it and fail. */
  Selector.prototype.applySize = function (row) {
    if (!this.sizeOption) return;              // cleared = leave the picker alone
    /* CORE carries the same S / M / L / CUSTOM MADE axis as PRO and takes its
       size from the same generation entry. It used to be left out here, so the
       CORE picker was hidden on its first value (S) for every car — an M or L
       car paid the S price and a made-to-order car paid no surcharge. */
    var rule = this.role ? this.ruleFor(this.role) : null;
    if ((rule === 'pro' || rule === 'core') && row) {
      var fit = proFit(row, this.state.gen);
      if (fit && fit.s) {
        this.sizeWant = { value: fit.s, only: false };
      } else if (rule === 'core' && row.core) {
        /* Flagged "core": true (Tesla Model 3 / S): CORE fits, but the data
           holds no size for it. S is what these cars always got before (the
           picker sat on its first value), so keep S — and set it explicitly,
           so a buyer who looked at a made-to-order car first does not carry
           the CUSTOM MADE price over. Give the model an "s" in the data to
           change it. */
        this.sizeWant = { value: row.s || 'S', only: false };
      } else {
        this.sizeWant = { value: this.customValue, only: true };
      }
    } else {
      this.sizeWant = null;                    // nothing to say -> stay hidden
    }
    this.paintSize();
    this.watchPicker();
  };

  Selector.prototype.paintSize = function () {
    if (!this.sizeOption) return;
    var want = this.sizeWant;

    /* A made-to-order build cannot use the stock lead times — the design
       phase alone is 2-3 weeks — so the calendar's earliest date is pushed out
       instead of the calendar being taken away. The buyer still picks a date,
       it is just a date we can actually hit. */
    this.paintCalendar(!!(want && want.only));

    var found = findOptionRow(this.sizeOption);   // re-find: the nodes may be new
    if (!found) return;

    if (!want) {
      showAllValues(found);
      found.row.style.display = 'none';
      this.sizeApplied = null;
      return;
    }

    var ok = setOptionValue(found, want.value);
    if (want.only && ok && showOnlyValue(found, want.value)) {
      found.row.style.display = '';            // visible, but only CUSTOM MADE
    } else {
      showAllValues(found);
      /* Hide only on success. A hidden control the buyer cannot reach is
         worse than an ugly one they can. */
      found.row.style.display = ok ? 'none' : '';
    }
    this.sizeApplied = ok ? want.value : null;
  };

  /* custom = true when the build is made to order.

     With a lead set (the default, 35 days = 5 weeks) the calendar keeps working
     and simply cannot offer anything sooner. With the lead set to 0 it falls
     back to hiding the calendar, which also needs delivery-calendar.js to stand
     down — see delivery-calendar-PATCH.md — because it hijacks the buy button
     and would otherwise demand a date the buyer can no longer see. */
  Selector.prototype.paintCalendar = function (custom) {
    var sel = this.cfg.calendarSelector;
    if (sel === undefined || sel === null) sel = '.delivery-calendar';
    if (!sel) return;

    var days = this.cfg.customLeadDays;
    if (days === undefined || days === null || days === '') days = 56;
    days = parseInt(days, 10);
    var cap = !isNaN(days) && days > 0;

    /* The one delivery option left standing while a build is made to order.
       Priority and Rush are withdrawn by the calendar, not renamed: a custom
       platform cannot be hurried, so offering to sell speed on it is a promise
       we would have to break. */
    var label = this.cfg.customWindowLabel;
    if (typeof label !== 'string' || !label.replace(/\s+/g, '')) label = 'Custom build';

    /* The far end of the made-to-order window. A floor with no ceiling is not a
       window: this product's Standard span is 25-30 days, so moving the start
       to day 56 and leaving the end at day 30 leaves nothing to pick. */
    var until = this.cfg.customLeadMaxDays;
    if (until === undefined || until === null || until === '') until = 84;
    until = parseInt(until, 10);
    if (isNaN(until) || until <= days) until = null;   // let the calendar work it out

    var wantFloor = (cap && custom) ? (String(days) + '-' + String(until) + '|' + label) : '';

    var found = $$(sel);
    /* Say what happened. Silence here is indistinguishable from "the patch is
       not installed", "the selector is wrong" and "it is broken", which is a
       miserable thing to debug. One line, once per page. */
    if (!found.length) {
      if (!window.__tgCalWarned) {
        window.__tgCalWarned = true;
        console.warn('[tg-selector] no delivery calendar matched "' + sel +
          '" on this page, so the made-to-order lead time is not being applied.');
      }
      return;
    }

    found.forEach(function (node) {
      if (!cap) {                                   // fall back to hiding
        node.style.display = custom ? 'none' : '';
        node.setAttribute('data-pdc-off', custom ? 'true' : 'false');
        return;
      }
      node.style.display = '';
      node.setAttribute('data-pdc-off', 'false');

      /* Two things make "has this already been applied?" harder than it looks.

         1. delivery-calendar.js is deferred, like this file, and nothing orders
            the two. If the selector runs first the hook does not exist yet.
         2. The calendar re-initialises on shopify:section:load, which builds a
            fresh closure and a fresh __pdcSetFloor. Everything it was told
            before that is gone, even though the DOM node — and any attribute
            written on it — survives.

         So the record of what was applied is the floor AND the identity of the
         function that applied it, and it is written only once the call has
         actually landed. Recording it any earlier, as this did, locks the
         calendar out for the life of the page the first time the race is lost:
         which is exactly what happened. */
      node.__tgWant = { key: wantFloor, days: (custom ? days : null), label: label, until: until };

      if (node.__tgFloorApplied === wantFloor && node.__tgFloorFn === node.__pdcSetFloor) return;
      if (node.__tgFloorPending) return;             // a retry loop is already chasing it
      node.__tgFloorPending = true;

      var tries = 0;
      (function attempt() {
        var want = node.__tgWant;
        if (node.__tgFloorApplied === want.key && node.__tgFloorFn === node.__pdcSetFloor) {
          node.__tgFloorPending = false;
          return;
        }
        if (typeof node.__pdcSetFloor === 'function') {
          node.__pdcSetFloor(want.days, want.label, want.until);
          node.__tgFloorApplied = want.key;
          node.__tgFloorFn = node.__pdcSetFloor;
          node.setAttribute('data-pdc-floor', want.key);   // for the inspector
          node.__tgFloorPending = false;
          return;
        }
        if (++tries > 40) {                          // ~6 seconds, then give up
          node.__tgFloorPending = false;
          if (!window.__tgCalWarned) {
            window.__tgCalWarned = true;
            console.warn('[tg-selector] delivery calendar found, but it never ' +
              'exposed a __pdcSetFloor hook — Edit A of delivery-calendar-PATCH.md ' +
              'is not installed in assets/delivery-calendar.js on THIS theme, so ' +
              'the made-to-order lead time cannot be applied.');
          }
          return;
        }
        setTimeout(attempt, 150);
      })();
    });
  };

  /* Repaint after Dawn swaps the picker out. childList only — setting a style
     or checking a radio changes attributes, not children, so this cannot feed
     itself. One repaint per frame at most. */
  Selector.prototype.watchPicker = function () {
    /* Every role needs this, not just PRO. Dawn's re-render wipes the hidden
       Car input and the disabled state of the Add to cart button as well as
       the option rows, so a LEVEL page without an observer loses all three —
       which is why the elevation pills flashed away and came straight back. */
    if (this.watching) return;
    if (typeof MutationObserver === 'undefined') return;
    var anchor = document.querySelector('variant-selects')
              || document.querySelector('form[action*="/cart/add"]');
    /* body, so a calendar rendered outside the form is covered too */
    var host = document.body || (anchor && anchor.parentNode);
    if (!host) return;

    var self = this, queued = false;
    this.observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      /* Dawn has just replaced the picker markup. Everything the selector had
         written into it is gone: the size, the elevation, and the hidden Car
         input that lives in the same form. Put all three back. */
      var run = function () {
        queued = false;
        self.paintSize();
        self.paintElev();
        self.writeCar();
        if (self.requireCar && self.role) self.gateAddToCart(self.gateState);
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run);
      else setTimeout(run, 16);
    });
    this.observer.observe(host, { childList: true, subtree: true });
    this.watching = true;
  };

  /* --------------------------------------------------------------- popups */
  function makeDialog(title, body) {
    var dlg = el('dialog', 'tg-vs__dialog');
    var head = el('div', 'tg-vs__dialoghead');
    head.appendChild(el('h3', 'tg-vs__dialogtitle', title));
    var close = el('button', 'tg-vs__close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    head.appendChild(close);
    dlg.appendChild(head);
    dlg.appendChild(body);
    close.addEventListener('click', function () {
      if (dlg.close) dlg.close(); else dlg.removeAttribute('open');
    });
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg && dlg.close) dlg.close();   // click the backdrop
    });
    return dlg;
  }
  function openerFor(dlg, cls, label) {
    var btn = el('button', cls, label);
    btn.type = 'button';
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.addEventListener('click', function () {
      if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
    });
    return btn;
  }

  /* ------------------------------------------------- alternatives, two ways */
  Selector.prototype.alsoFitsLink = function (roles, car) {
    var wrap = el('p', 'tg-vs__also');
    var dlg = makeDialog('More for your ' + car, this.fitCards(roles, false));
    wrap.appendChild(openerFor(dlg, 'tg-vs__alsolink', 'More products that fit your car'));
    wrap.appendChild(dlg);
    return wrap;
  };

  /* Made to order: say so plainly, and explain the process behind a popup so
     the promise is not just a colour. */
  Selector.prototype.madeToOrderBlock = function (car) {
    var cfg = this.cfg;
    var wrap = el('div', 'tg-vs__made');
    /* The lead names the product. On the CORE page the made-to-order build is
       a CORE, so "PRO can be made custom" was telling a CORE buyer about the
       wrong product. */
    var lead = cfg.customLead || CUSTOM_LEAD;
    if (this.role && this.ruleFor(this.role) === 'core') lead = lead.replace(/\bPRO\b/g, 'CORE');
    wrap.appendChild(el('p', 'tg-vs__madelead', lead));

    var body = el('div', 'tg-vs__steps');
    var intro = cfg.customIntro || CUSTOM_INTRO;
    if (intro) body.appendChild(el('p', 'tg-vs__stepsintro', intro));
    var steps = (cfg.customSteps && cfg.customSteps.length) ? cfg.customSteps : CUSTOM_STEPS;
    var ol = el('ol', 'tg-vs__steplist');
    steps.forEach(function (t) { ol.appendChild(el('li', null, t)); });
    body.appendChild(ol);

    var dlg = makeDialog('Custom made for your ' + car, body);
    wrap.appendChild(openerFor(dlg, 'tg-vs__madelink', cfg.customCta || CUSTOM_CTA));
    wrap.appendChild(dlg);
    return wrap;
  };

  /* mailto rather than a contact form: one tap on a phone, the car is already
     in the subject, and the reply lands in a thread they can answer. */
  /* Which fitment rule judges this slot. Falls back to the slot's own name, so
     every card that has not been remapped behaves exactly as before. */
  Selector.prototype.ruleFor = function (slot) {
    return (this.rules && this.rules[slot]) || slot;
  };

  Selector.prototype.askUsLine = function (car) {
    var wrap = el('p', 'tg-vs__ask');
    var a = el('a', 'tg-vs__asklink', this.askMsg);
    a.href = 'mailto:' + (this.askEmail || 'info@teraglide.com') +
             '?subject=' + encodeURIComponent('Does it fit my ' + car + '?') +
             '&body=' + encodeURIComponent('My car: ' + car + '\n\n');
    wrap.appendChild(a);
    return wrap;
  };

  Selector.prototype.fitCards = function (roles, withHeading) {
    var self = this;
    var wrap = el('div', 'tg-vs__fits');
    if (withHeading) {
      wrap.appendChild(el('p', 'tg-vs__fitslead', roles.length === 1
        ? 'This platform fits your car'
        : 'These platforms fit your car'));
    }
    var grid = el('div', 'tg-vs__cards');
    roles.forEach(function (r) {
      var p = self.products[r];
      var a = el('a', 'tg-vs__card');
      a.href = p.url;
      if (p.image) {
        var img = el('img', 'tg-vs__cardimg');
        img.src = p.image;
        img.alt = '';
        img.loading = 'lazy';
        a.appendChild(img);
      } else {
        a.appendChild(el('span', 'tg-vs__cardimg tg-vs__cardimg--none'));
      }
      a.appendChild(el('span', 'tg-vs__cardname', p.title));
      /* Optional. A card with no description keeps the old layout exactly. */
      if (p.desc) a.appendChild(el('span', 'tg-vs__carddesc', p.desc));
      grid.appendChild(a);
    });
    wrap.appendChild(grid);
    return wrap;
  };

  Selector.prototype.customLine = function () {
    var wrap = el('div', 'tg-vs__fits');
    wrap.appendChild(el('p', 'tg-vs__fitslead', 'We have not built for this car yet'));
    var grid = el('div', 'tg-vs__cards');
    var a = el('a', 'tg-vs__card tg-vs__card--custom');
    a.href = (this.cfg.customUrl || '/products/car-camping-platforms-custom-made') +
             '?car=' + encodeURIComponent(this.carName());
    a.appendChild(el('span', 'tg-vs__cardimg tg-vs__cardimg--none'));
    a.appendChild(el('span', 'tg-vs__cardname', 'Ask for a custom build'));
    grid.appendChild(a);
    wrap.appendChild(grid);
    return wrap;
  };

  /* The chosen car, as a line-item property, so it reaches the cart, the
     checkout, the confirmation email, the admin order and the packing slip.
     No underscore on the name: that is exactly what makes it visible in all
     of those rather than admin-only.

     WHY THIS IS MORE THAN ONE LINE
     Dawn REPLACES the product form when the variant changes. The hidden input
     went with it, and syncForm only ran when the CAR changed, so the sequence
     "pick car, pick variant, add to cart" shipped an order with no car on it.
     Every order in the store is missing it. So the decision is remembered, and
     rewritten: on every repaint, and again in the capture phase of submit and
     of the add-to-cart click — which fires before the delivery calendar's own
     handler reads the form, because document capture precedes form capture. */
  Selector.prototype.syncForm = function (on) {
    this.wantCar = on ? this.carName() : null;
    this.writeCar();
    this.guardForm();
  };

  Selector.prototype.writeCar = function () {
    var want = this.wantCar;
    $$('form[action*="/cart/add"]').forEach(function (form) {
      var input = form.querySelector('input[name="properties[' + PROP + ']"]');
      if (!want) {
        if (input && input.parentNode) input.parentNode.removeChild(input);
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + PROP + ']';
        form.appendChild(input);
      }
      if (input.value !== want) input.value = want;
    });
  };

  Selector.prototype.guardForm = function () {
    if (this.guarded) return;
    this.guarded = true;
    var self = this;
    document.addEventListener('submit', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('form[action*="/cart/add"]')) self.writeCar();
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[name="add"], .product-form__submit, button[type="submit"]')) {
        self.writeCar();
      }
    }, true);
  };

  /* ------------------------------------------------------------------ boot */
  function boot() {
    var data = readJSON('tg-vehicle-data');
    if (!data) return;
    $$('[data-tg-vehicle-selector]').forEach(function (root) {
      if (root.__tgDone) return;
      var cfg = readJSON(root.getAttribute('data-config-id'));
      if (!cfg) return;
      root.__tgDone = true;
      root.__tgInstance = new Selector(root, data, cfg);
    });
  }

  /* Both this file and delivery-calendar.js listen for section reloads, and
     nothing orders those two listeners either. Boot now for the selector, then
     once more on the next tick so that if the calendar re-initialised after us
     — new closure, new hook, floor forgotten — we notice and re-apply it. */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e);
    setTimeout(function () {
      $$('[data-tg-vehicle-selector]').forEach(function (root) {
        if (root.__tgInstance) {
          root.__tgInstance.paintSize();
          root.__tgInstance.paintElev();
          root.__tgInstance.writeCar();
          if (root.__tgInstance.requireCar && root.__tgInstance.role) {
            root.__tgInstance.gateAddToCart(root.__tgInstance.gateState);
          }
        }
      });
    }, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();